import { FC, useCallback, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'

import {
  FollowedVenuesQuery,
  FollowVenueMutation,
  FollowVenueMutationVariables,
  UnfollowVenueMutation,
  UnfollowVenueMutationVariables,
  UserRoleNameEnum,
} from 'gql'
import { useDataLayer, useQueryParams } from '~hooks'
import { notifySuccess } from '~shared'
import { FOLLOW_VENUE, UNFOLLOW_VENUE } from 'gql/mutations'
import { USER_FOLLOWED_VENUES } from 'gql/user/mutations'
import { FollowProps } from '../types'
import FollowButton from '../button'
import ModalSignUp from '../signup'

const FollowVenue: FC<FollowProps> = ({ id, slug, starName }) => {
  const { push, paths } = useQueryParams()
  const dl = useDataLayer()

  const [isModalOpen, setIsModalOpen] = useState(false)

  const { data, loading } = useQuery<FollowedVenuesQuery>(USER_FOLLOWED_VENUES)

  const [followVenue, { loading: followLoading }] = useMutation<
    FollowVenueMutation,
    FollowVenueMutationVariables
  >(FOLLOW_VENUE, {
    refetchQueries: ['FollowedVenues'],
  })

  const [unfollowVenue, { loading: unfollowLoading }] = useMutation<
    UnfollowVenueMutation,
    UnfollowVenueMutationVariables
  >(UNFOLLOW_VENUE, {
    refetchQueries: ['FollowedVenues'],
  })

  const isFollowed = useMemo(() => {
    if (!data?.currentUser) {
      return false
    }

    return !!data?.currentUser.followedVenues.edges.find(
      ({ node: { id: followedId } }) => followedId === id
    )
  }, [data?.currentUser, id])

  const onFollowChange = useCallback(() => {
    if (data?.currentUser && data?.currentUser?.type?.name !== UserRoleNameEnum.Guest) {
      if (isFollowed) {
        dl.push({ event: 'unfollow_star_venue' })
        unfollowVenue({
          variables: {
            id,
          },
        })
      } else {
        dl.push({ event: 'follow_star_venue' })
        followVenue({
          variables: {
            id,
          },
        })
      }
    } else {
      setIsModalOpen(true)
    }
  }, [data?.currentUser, followVenue, id, isFollowed, paths, push, slug, unfollowVenue])

  const follow = () => {
    dl.push({ event: 'follow_star_venue' })
    followVenue({
      variables: {
        id,
      },
    })
    setIsModalOpen(false)
    notifySuccess(`You're following the venue.`)
  }

  const canFollowVenue =
    id && data?.currentUser && data?.currentUser?.type?.name !== UserRoleNameEnum.Guest

  return (
    <>
      <FollowButton
        onClick={onFollowChange}
        isFollowed={isFollowed}
        disabled={followLoading || unfollowLoading || loading}
      />
      <ModalSignUp
        follow={follow}
        starName={starName}
        isOpen={isModalOpen}
        canFollow={canFollowVenue}
        setIsOpen={setIsModalOpen}
        redirectUrl={paths.VENUE(slug)}
      />
    </>
  )
}

export default FollowVenue
